import React, { useState, useMemo } from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
} from 'recharts';
import { Holding, Transaction } from '../../types';
import { CardHeader } from '../ui/Card';
import { useCurrency } from '../../context/CurrencyContext';
import { Wallet } from 'lucide-react';

interface WalletAllocationChartProps {
  holdings: Holding[];
  transactions: Transaction[];
}

interface WalletSlice {
  wallet: string;
  value: number;
  pct: number;
  color: string;
}

const WALLET_COLORS = [
  '#10b981', // emerald
  '#3b82f6', // blue
  '#f59e0b', // amber
  '#06b6d4', // cyan
  '#ec4899', // pink
  '#ef4444', // red
  '#84cc16', // lime
  '#f97316', // orange
  '#14b8a6', // teal
  '#38bdf8', // sky
  '#a0a0a0', // grey
  '#6b7280', // dark grey
];

function computeWalletSlices(transactions: Transaction[], holdings: Holding[]): WalletSlice[] {
  // Build current price map from holdings
  const priceMap: Record<string, number> = {};
  holdings.forEach((h) => { priceMap[h.coinId] = h.currentPrice; });

  // Accumulate net quantity per wallet per coin.
  // Transfers move coins between wallets — net-zero across the portfolio.
  const walletCoinQty: Record<string, Record<string, number>> = {};
  const bump = (wallet: string, coinId: string, delta: number) => {
    if (!walletCoinQty[wallet]) walletCoinQty[wallet] = {};
    walletCoinQty[wallet][coinId] = (walletCoinQty[wallet][coinId] ?? 0) + delta;
  };
  transactions.forEach((tx) => {
    if (tx.type === 'TRANSFER') {
      if (tx.fromWallet) bump(tx.fromWallet, tx.coinId, -tx.amount);
      if (tx.toWallet)   bump(tx.toWallet,   tx.coinId,  tx.amount);
      return;
    }
    const wallet = tx.wallet?.trim() || 'Unspecified';
    bump(wallet, tx.coinId, tx.type === 'BUY' ? tx.amount : -tx.amount);
  });

  // Convert net quantities to current values
  const walletValues: Record<string, number> = {};
  Object.entries(walletCoinQty).forEach(([wallet, coinQtys]) => {
    let total = 0;
    Object.entries(coinQtys).forEach(([coinId, qty]) => {
      if (qty > 0) total += qty * (priceMap[coinId] ?? 0);
    });
    if (total > 0) walletValues[wallet] = total;
  });

  const totalValue = Object.values(walletValues).reduce((s, v) => s + v, 0);
  if (totalValue === 0) return [];

  // Sort by value descending, "Unspecified" always last
  return Object.entries(walletValues)
    .sort(([aName, aVal], [bName, bVal]) => {
      if (aName === 'Unspecified') return 1;
      if (bName === 'Unspecified') return -1;
      return bVal - aVal;
    })
    .map(([wallet, value], i) => ({
      wallet,
      value,
      pct: (value / totalValue) * 100,
      color: WALLET_COLORS[i % WALLET_COLORS.length],
    }));
}

export function WalletAllocationChart({ holdings, transactions }: WalletAllocationChartProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const { fmt } = useCurrency();

  const slices = useMemo(
    () => computeWalletSlices(transactions, holdings),
    [transactions, holdings]
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload as WalletSlice;
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 shadow-xl">
        <p className="text-sm font-bold text-slate-100 mb-1">{d.wallet}</p>
        <p className="text-xs text-slate-400">{fmt(d.value)}</p>
        <p className="text-xs text-slate-400">{d.pct.toFixed(1)}% of portfolio</p>
      </div>
    );
  };

  if (slices.length === 0) {
    return (
      <div>
        <CardHeader title="Wallet Allocation" subtitle="Portfolio distribution by wallet" />
        <div className="flex flex-col items-center justify-center h-48 text-center">
          <Wallet className="w-8 h-8 text-slate-700 mb-3" />
          <p className="text-slate-500 text-sm">No wallet data yet</p>
          <p className="text-slate-600 text-xs mt-1">Assign wallets to your transactions to see the breakdown</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <CardHeader title="Wallet Allocation" subtitle="By wallet" />
      <div className="flex flex-col sm:flex-row items-center gap-6">
        {/* Donut chart */}
        <div className="flex-shrink-0 w-48 h-48">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={slices}
                cx="50%"
                cy="50%"
                innerRadius={52}
                outerRadius={76}
                paddingAngle={2}
                dataKey="value"
                onMouseEnter={(_, idx) => setActiveIndex(idx)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                {slices.map((entry, i) => (
                  <Cell
                    key={entry.wallet}
                    fill={entry.color}
                    opacity={activeIndex === null || activeIndex === i ? 1 : 0.4}
                    stroke="transparent"
                  />
                ))}
              </Pie>
              <Tooltip content={renderTooltip} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Legend list */}
        <div className="flex-1 w-full space-y-2.5">
          {slices.map((s, i) => (
            <div
              key={s.wallet}
              className="flex items-center gap-3 group cursor-default"
              onMouseEnter={() => setActiveIndex(i)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              <div
                className="w-2.5 h-2.5 rounded-full flex-shrink-0 transition-transform group-hover:scale-125"
                style={{ backgroundColor: s.color }}
              />
              <span className="text-sm font-medium text-slate-300 flex-1 truncate">{s.wallet}</span>
              <div className="w-20 h-1.5 bg-slate-800 rounded-full overflow-hidden flex-shrink-0">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${s.pct}%`, backgroundColor: s.color }}
                />
              </div>
              <span className="text-xs text-slate-500 w-16 text-right flex-shrink-0 font-mono">{fmt(s.value)}</span>
              <span className="text-sm text-slate-400 w-12 text-right flex-shrink-0">{s.pct.toFixed(1)}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
